import React, { useMemo, useRef, useState } from 'react'
import { FolderOpen, Upload, X } from 'lucide-react'
import toast from 'react-hot-toast'

import { useAuth } from '@/context/AuthContext'
import {
  getMedicalDocumentFileKey,
  getMedicalDocumentSourcePath,
  isAcceptedMedicalPdfFile
} from '@/lib/medical-document-storage'
import { simpleUploadMultiple } from '@/lib/working-simple-upload'

interface WorkingUploaderProps {
  onClose: () => void
  onComplete?: () => void
}

interface FileStatus {
  id: string
  file: File
  displayPath: string
  status:
    | 'pending'
    | 'checking'
    | 'uploading'
    | 'analyzing'
    | 'saving'
    | 'success'
    | 'duplicate'
    | 'error'
  progress: number
  error?: string
}

type DirectoryEnabledInput = HTMLInputElement & {
  webkitdirectory?: boolean
  mozdirectory?: boolean
  directory?: boolean
}

type PickerWindow = Window & {
  showDirectoryPicker?: () => Promise<any>
}

interface SelectedPdfFiles {
  acceptedFiles: File[]
  ignoredFilesCount: number
}

function toFileStatus(file: File): FileStatus {
  return {
    id: getMedicalDocumentFileKey(file),
    file,
    displayPath: getMedicalDocumentSourcePath(file),
    status: 'pending',
    progress: 0
  }
}

function withRelativePath(file: File, relativePath: string): File {
  const clonedFile = new File([file], file.name, {
    type: file.type,
    lastModified: file.lastModified
  })

  Object.defineProperty(clonedFile, 'webkitRelativePath', {
    value: relativePath,
    configurable: true
  })

  return clonedFile as File
}

function filterAcceptedPdfFiles(fileList: FileList | File[] | null): SelectedPdfFiles {
  const selectedFiles = Array.from(fileList || [])
  const acceptedFiles = selectedFiles.filter((file) => isAcceptedMedicalPdfFile(file))

  return {
    acceptedFiles,
    ignoredFilesCount: selectedFiles.length - acceptedFiles.length
  }
}

async function collectPdfFilesFromDirectoryHandle(
  directoryHandle: any,
  currentPath = ''
): Promise<SelectedPdfFiles> {
  const files: File[] = []
  let ignoredFilesCount = 0

  for await (const [entryName, entryHandle] of directoryHandle.entries()) {
    const relativePath = currentPath ? `${currentPath}/${entryName}` : entryName

    if (entryHandle.kind === 'directory') {
      const nestedResult = await collectPdfFilesFromDirectoryHandle(entryHandle, relativePath)
      files.push(...nestedResult.acceptedFiles)
      ignoredFilesCount += nestedResult.ignoredFilesCount
      continue
    }

    if (entryHandle.kind !== 'file') {
      continue
    }

    const file = await entryHandle.getFile()
    const candidateFile = withRelativePath(file, relativePath)

    if (isAcceptedMedicalPdfFile(candidateFile)) {
      files.push(withRelativePath(file, relativePath))
      continue
    }

    if (file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')) {
      ignoredFilesCount += 1
    }
  }

  return {
    acceptedFiles: files,
    ignoredFilesCount
  }
}

export function WorkingUploader({ onClose, onComplete }: WorkingUploaderProps) {
  const { user } = useAuth()
  const [files, setFiles] = useState<FileStatus[]>([])
  const [isUploading, setIsUploading] = useState(false)
  const [isCollectingFolder, setIsCollectingFolder] = useState(false)
  const [completedSummary, setCompletedSummary] = useState<{
    importedCount: number
    duplicateCount: number
    failedCount: number
  } | null>(null)
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const mergeFiles = (selectedFiles: File[]) => {
    if (selectedFiles.length === 0) {
      return
    }

    setCompletedSummary(null)

    setFiles((previousFiles) => {
      const existingIds = new Set(previousFiles.map((file) => file.id))
      const nextFiles = selectedFiles
        .map(toFileStatus)
        .filter((file) => !existingIds.has(file.id))

      if (nextFiles.length < selectedFiles.length) {
        toast(`${selectedFiles.length - nextFiles.length} doublon(s) ignores dans la selection`)
      }

      return [...previousFiles, ...nextFiles]
    })
  }

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { acceptedFiles, ignoredFilesCount } = filterAcceptedPdfFiles(event.target.files)
    mergeFiles(acceptedFiles)

    if (ignoredFilesCount > 0) {
      toast(`${ignoredFilesCount} fichier(s) systeme ignores`)
    }

    event.target.value = ''
  }

  const openFolderInputFallback = () => {
    const input = document.createElement('input') as DirectoryEnabledInput
    input.type = 'file'
    input.multiple = true
    input.accept = '.pdf'
    input.webkitdirectory = true
    input.mozdirectory = true
    input.directory = true
    input.setAttribute('webkitdirectory', '')
    input.setAttribute('directory', '')

    input.onchange = () => {
      const { acceptedFiles, ignoredFilesCount } = filterAcceptedPdfFiles(input.files)
      mergeFiles(acceptedFiles)

      if (ignoredFilesCount > 0) {
        toast(`${ignoredFilesCount} fichier(s) systeme ignores`)
      }
    }

    input.click()
  }

  const selectFolder = async () => {
    const pickerWindow = window as PickerWindow

    if (typeof pickerWindow.showDirectoryPicker === 'function') {
      try {
        setIsCollectingFolder(true)
        const directoryHandle = await pickerWindow.showDirectoryPicker()
        const { acceptedFiles, ignoredFilesCount } =
          await collectPdfFilesFromDirectoryHandle(directoryHandle)
        mergeFiles(acceptedFiles)

        if (ignoredFilesCount > 0) {
          toast(`${ignoredFilesCount} fichier(s) systeme ignores`)
        }

        if (acceptedFiles.length === 0) {
          toast('Aucun PDF trouve dans ce dossier')
        }

        return
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') {
          return
        }

        console.warn('Selection de dossier native indisponible, fallback navigateur utilise.', error)
      } finally {
        setIsCollectingFolder(false)
      }
    }

    openFolderInputFallback()
  }

  const startUpload = async () => {
    if (!user || files.length === 0) {
      return
    }

    setIsUploading(true)

    try {
      const results = await simpleUploadMultiple(
        files.map((file) => file.file),
        user.id,
        (fileKey, progress) => {
          setFiles((previousFiles) =>
            previousFiles.map((file) =>
              file.id === fileKey
                ? {
                    ...file,
                    status: progress.status,
                    progress: progress.progress,
                    error: progress.error
                  }
                : file
            )
          )
        },
        {
          concurrency: files.length >= 12 ? 4 : 3
        }
      )

      const importedCount = results.filter((result) => result.success && !result.duplicate).length
      const duplicateResultsCount = results.filter((result) => result.duplicate).length
      const failedCount = results.filter((result) => !result.success && !result.duplicate).length

      setCompletedSummary({
        importedCount,
        duplicateCount: duplicateResultsCount,
        failedCount
      })

      toast.success(
        `${importedCount} importes, ${duplicateResultsCount} doublons, ${failedCount} erreurs`
      )
      onComplete?.()
    } catch (error) {
      console.error('Upload failed:', error)
      toast.error(error instanceof Error ? error.message : 'Erreur pendant l import')
    } finally {
      setIsUploading(false)
    }
  }

  const removeFile = (fileId: string) => {
    setCompletedSummary(null)
    setFiles((previousFiles) => previousFiles.filter((file) => file.id !== fileId))
  }

  const successCount = useMemo(
    () => files.filter((file) => file.status === 'success').length,
    [files]
  )
  const duplicateCount = useMemo(
    () => files.filter((file) => file.status === 'duplicate').length,
    [files]
  )
  const errorCount = useMemo(
    () => files.filter((file) => file.status === 'error').length,
    [files]
  )
  const totalProgress = useMemo(() => {
    if (files.length === 0) {
      return 0
    }

    return Math.round(files.reduce((total, file) => total + file.progress, 0) / files.length)
  }, [files])

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="max-h-[80vh] w-full max-w-2xl overflow-auto rounded-lg bg-white">
        <div className="flex items-center justify-between border-b p-4">
          <div>
            <h2 className="text-lg font-semibold">Dossier maitre</h2>
            <p className="text-sm text-gray-600">PDF only. Sous-dossiers inclus.</p>
          </div>
          <button type="button" onClick={onClose} className="rounded p-1 hover:bg-gray-100">
            <X size={20} />
          </button>
        </div>

        <div className="p-4">
          {files.length > 0 && (
            <div className="mb-4 rounded bg-gray-50 p-3 text-sm">
              <div>
                {files.length} fichiers - {successCount} importes - {duplicateCount} doublons - {errorCount} erreurs
              </div>
              <div className="mt-2 h-2 w-full rounded-full bg-gray-200">
                <div
                  className="h-2 rounded-full bg-blue-500 transition-all duration-300"
                  style={{ width: `${totalProgress}%` }}
                />
              </div>
              <p className="mt-2 text-xs text-gray-500">Sous-dossiers et fichiers caches ignores automatiquement.</p>
            </div>
          )}

          {completedSummary && (
            <div className="mb-4 rounded border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
              {completedSummary.importedCount} importes, {completedSummary.duplicateCount} doublons, {completedSummary.failedCount} erreurs.
            </div>
          )}

          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".pdf"
            onChange={handleFileSelect}
            className="hidden"
          />

          {files.length === 0 ? (
            <div className="py-8 text-center">
              <Upload className="mx-auto mb-4 text-gray-400" size={48} />
              <p className="mb-2 text-gray-700">PDF et dossier complet</p>
              <p className="mb-6 text-sm text-gray-500">Le dossier maitre peut contenir des sous-dossiers.</p>

              <div className="flex flex-wrap items-center justify-center gap-3">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="rounded bg-blue-500 px-4 py-2 text-white hover:bg-blue-600"
                >
                  Selectionner des fichiers
                </button>

                <button
                  type="button"
                  onClick={() => {
                    void selectFolder()
                  }}
                  disabled={isCollectingFolder}
                  className="rounded bg-slate-700 px-4 py-2 text-white hover:bg-slate-800 disabled:opacity-60"
                >
                  <FolderOpen className="mr-2 inline h-4 w-4" />
                  {isCollectingFolder ? 'Lecture du dossier...' : 'Importer un dossier'}
                </button>
              </div>
            </div>
          ) : (
            <div>
              <div className="mb-4 max-h-60 space-y-2 overflow-y-auto">
                {files.map((file) => (
                  <div key={file.id} className="flex items-center gap-3 rounded bg-gray-50 p-2">
                    <div
                      className={`h-3 w-3 rounded-full ${
                        file.status === 'pending'
                          ? 'bg-gray-300'
                          : file.status === 'checking'
                            ? 'animate-pulse bg-violet-500'
                            : file.status === 'uploading'
                              ? 'animate-pulse bg-blue-500'
                              : file.status === 'analyzing'
                                ? 'animate-pulse bg-amber-500'
                                : file.status === 'saving'
                                  ? 'animate-pulse bg-cyan-500'
                                  : file.status === 'success'
                                    ? 'bg-green-500'
                                    : file.status === 'duplicate'
                                      ? 'bg-slate-500'
                                      : 'bg-red-500'
                      }`}
                    />

                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium">{file.file.name}</div>
                      <div className="truncate text-xs text-gray-500">
                        {file.displayPath !== file.file.name ? `${file.displayPath} - ` : ''}
                        {(file.file.size / 1024 / 1024).toFixed(1)} MB
                        {file.error ? ` - ${file.error}` : ''}
                      </div>
                    </div>

                    {(file.status === 'checking' ||
                      file.status === 'uploading' ||
                      file.status === 'analyzing' ||
                      file.status === 'saving') && (
                      <div className="text-xs">
                        {file.status === 'checking' && `Verification ${file.progress}%`}
                        {file.status === 'uploading' && `Upload ${file.progress}%`}
                        {file.status === 'analyzing' && `Analyse ${file.progress}%`}
                        {file.status === 'saving' && `Sauvegarde ${file.progress}%`}
                      </div>
                    )}

                    {file.status === 'pending' && !isUploading && (
                      <button
                        type="button"
                        onClick={() => removeFile(file.id)}
                        className="rounded p-1 text-gray-500 hover:bg-gray-200 hover:text-gray-700"
                      >
                        <X size={16} />
                      </button>
                    )}
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading}
                  className="rounded bg-gray-100 px-3 py-2 text-sm hover:bg-gray-200 disabled:opacity-50"
                >
                  Ajouter des fichiers
                </button>

                <button
                  type="button"
                  onClick={() => {
                    void selectFolder()
                  }}
                  disabled={isUploading || isCollectingFolder}
                  className="rounded bg-gray-100 px-3 py-2 text-sm hover:bg-gray-200 disabled:opacity-50"
                >
                  {isCollectingFolder ? 'Lecture...' : 'Ajouter un dossier'}
                </button>

                <div className="flex-1" />

                <button
                  type="button"
                  onClick={startUpload}
                  disabled={isUploading}
                  className="rounded bg-green-500 px-4 py-2 text-white hover:bg-green-600 disabled:opacity-50"
                >
                  {isUploading ? 'Import...' : `Importer ${files.length} PDF`}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
